import { useCallback } from 'react';
import { type Route, type RouteSegments, useNavigation } from 'expo-router';
import { Platform } from 'react-native';

import { useSafeIsNavigationReady } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a type-safe `reset` navigation function for the Expo Router setup.
 */
export const useSafeExpoNavigationReset = () => {
  const isNavigationReady = useSafeIsNavigationReady();

  try {
    const navigation = useNavigation('/');

    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback(
      (state: ResetState) => {
        if (!isNavigationReady) return;

        if (Platform.OS === 'web') {
          navigation.reset(state as never);
          return;
        }

        // NOTE: We defer on native to prevent conflicts between
        //       multiple modals.
        requestIdleCallback(() => {
          setTimeout(() => {
            navigation.reset(state as never);
          });
        });
      },
      [isNavigationReady, navigation],
    );
  } catch {
    return null;
  }
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetRoute = {
  name: RootLevelRouteName;
  state?: {
    routes: {
      name: ResetRouteName;
      params?: Record<string, unknown>;
      state?: { routes: { name: ResetRouteName; params?: Record<string, unknown> }[] }; // prettier-ignore
    }[];
  };
  params?: Record<string, string>;
};

type RootLevelRouteName = RouteSegments<Route>[number];
type ResetRouteName = RootLevelRouteName | NormalizedRoute;

type ResetState = {
  index?: number;
  routes: ResetRoute[];
};

type NormalizedRoute = Route extends `/${infer R}` ? R | `${R}/index` : never;
